import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useLanguage } from '../../hooks/useLanguage';

const AddAnimeModal = ({ onSave, onCancel, loading }) => {
  const { t } = useLanguage();
  const [newAnime, setNewAnime] = useState({
    title: '',
    subtitle: '',
    year: new Date().getFullYear(),
    season: t('springValue'),
    totalEpisodes: 12,
    startEpisode: 1,
    ratingMode: 'simple'
  });

  const handleSave = () => {
    if (!newAnime.title.trim()) return;
    onSave(newAnime);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold dark:text-gray-100">{t('addAnime')}</h2>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('title')}</label>
            <input
              type="text"
              value={newAnime.title}
              onChange={(e) => setNewAnime({ ...newAnime, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder={t('title')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('subtitle')}</label>
            <input
              type="text"
              value={newAnime.subtitle}
              onChange={e => setNewAnime({ ...newAnime, subtitle: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder={`${t('example')} 完全新作編 ほか`}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('broadcastYear')}</label>
              <input
                type="number"
                value={newAnime.year}
                onChange={(e) => setNewAnime({ ...newAnime, year: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('seasonDisplay')}</label>
              <select
                value={newAnime.season}
                onChange={(e) => setNewAnime({ ...newAnime, season: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value={t('winterValue')}>{t('winter')}</option>
                <option value={t('springValue')}>{t('spring')}</option>
                <option value={t('summerValue')}>{t('summer')}</option>
                <option value={t('autumnValue')}>{t('autumn')}</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('totalEpisodes')}</label>
              <input
                type="number"
                min="1"
                value={newAnime.totalEpisodes}
                onChange={(e) => setNewAnime({ ...newAnime, totalEpisodes: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('startEpisode')}</label>
              <input
                type="number"
                min="0"
                value={newAnime.startEpisode}
                onChange={(e) => setNewAnime({ ...newAnime, startEpisode: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder={`0 = ${t('episode', { number: 0 })}から`}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">評価モード</label>
            <select
              value={newAnime.ratingMode}
              onChange={(e) => setNewAnime({ ...newAnime, ratingMode: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="simple">{t('simpleRating')}</option>
              <option value="detailed">{t('detailedRating')}</option>
            </select>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t('cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={!newAnime.title.trim() || loading}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? t('saving') : t('add')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddAnimeModal;